import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Tv, Lock, ChevronDown, Tag, BarChart3, MapPin, LayoutDashboard, List } from 'lucide-react'
import { Card, Tooltip } from '../../ui'
import useGlobalCustomerStore from '../../../stores/globalCustomerStore'
import { useToast } from '../../../contexts/ToastContext'
import broadcastService, { PublishedBroadcast } from '../../../services/broadcastService'
import { formatDuration, formatEventDate, formatChannelType, formatViewers, describeViewers } from './broadcastCustomerFormat'
import BroadcastAllowanceBanner from './BroadcastAllowanceBanner'
import BroadcastLibraryTotals from './BroadcastLibraryTotals'
import BroadcastValueDonuts from './BroadcastValueDonuts'
import BroadcastSponsorCards from './BroadcastSponsorCards'
import BroadcastToolbar from '../BroadcastToolbar'
import BroadcastPagination from '../BroadcastPagination'
import { currencyOf, rowFigures, formatAreaSeconds, formatMoney } from '../broadcastValuation'

type SortField =
  | 'title' | 'event_date'
  // Added with the value columns. Length lost its own sort when it folded
  // under Aired: with value on the row, "which was longest" stopped being
  // the question people sort by, and the Footage card carries the total.
  | 'sponsors' | 'area_seconds' | 'presence' | 'value' | 'viewers'
type SortOrder = 'asc' | 'desc'

const th = 'text-[10px] font-semibold text-gray-600 uppercase tracking-wider'
const PER_PAGE = 25

type Tab = 'overview' | 'broadcasts'
const TABS = [
  { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
  { id: 'broadcasts' as const, label: 'Broadcasts', icon: List }
]

/**
 * Published broadcasts, customer side.
 *
 * Only ever shows what a reviewer has signed off — the API enforces that, not
 * this component. An empty list here means nothing has been published yet,
 * which is a different thing from the customer having no broadcasts.
 *
 * Sorting is client-side because the endpoint returns the whole library: a
 * customer's published broadcasts are counted in tens, not thousands.
 *
 * Two tabs: Overview (the library's totals and donuts) and Broadcasts (the
 * table). The tab lives in the URL, ?tab=broadcasts, so the back button on a
 * broadcast returns to the table it was opened from rather than the overview.
 * Both tabs render from the one component, so switching keeps the search,
 * sort and page.
 */
const BroadcastsPage: React.FC = () => {
  const navigate = useNavigate()
  const { selectedCustomer } = useGlobalCustomerStore()
  const { showError } = useToast()
  const [items, setItems] = useState<PublishedBroadcast[]>([])
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)
  const [sortBy, setSortBy] = useState<SortField>('event_date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: Tab = searchParams.get('tab') === 'broadcasts' ? 'broadcasts' : 'overview'
  const selectTab = (t: Tab) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true })

  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''

  const load = useCallback(async () => {
    if (!customerId) { setLoading(false); return }
    setLoading(true)
    try {
      const res = await broadcastService.listBroadcasts(customerId)
      setItems(res.data || [])
      setLocked(false)
    } catch (err) {
      // A 403 here is the feature gate, which is a state to render rather than
      // a failure to toast — toasting it put an error over a page that
      // otherwise looked fine, which is what it did before.
      const status = (err as { status?: number })?.status
      if (status === 403) {
        setLocked(true)
      } else {
        showError('Could not load broadcasts', err instanceof Error ? err.message : 'Please try again')
      }
    } finally {
      setLoading(false)
    }
  }, [customerId, showError])

  useEffect(() => { void load() }, [load])

  const refresh = async () => {
    setRefreshing(true)
    try { await load() } finally { setRefreshing(false) }
  }

  // Any narrowing of the set invalidates the page number — staying on page 3
  // of a result set that now has one page shows an empty table.
  useEffect(() => { setPage(1) }, [search, sortBy, sortOrder])

  const handleSort = (field: SortField) => {
    if (field === sortBy) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      // Text reads A-Z; every measure reads biggest first, which is the
      // answer someone is looking for when they click it.
      setSortOrder(field === 'title' ? 'asc' : 'desc')
    }
  }

  // Searches what the table actually shows, so a hit always explains itself.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((b) => [
      b.title, b.venue_name, b.league_short_name, b.competition_stage_name, b.season, b.channel_name
    ].some((f) => f?.toLowerCase().includes(q)))
  }, [items, search])

  const sorted = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1
    const key = (x: PublishedBroadcast): string | number | null => {
      const f = rowFigures(x)
      switch (sortBy) {
        case 'event_date': return x.event_date
        case 'viewers':    return x.viewers == null ? null : Number(x.viewers)
        case 'sponsors':   return x.sponsored_brands ?? null
        case 'area_seconds': return x.sponsored_area_seconds ?? null
        case 'presence':   return f.presence
        case 'value':      return f.value
        default:           return null
      }
    }
    return [...filtered].sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title) * dir
      const av = key(a); const bv = key(b)
      // Nulls sort last whichever way the column points — an unpriced
      // broadcast is missing information, not the cheapest one.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir
    })
  }, [filtered, sortBy, sortOrder])

  // The area bar's scale. Taken over the FILTERED set, not the page, so a
  // broadcast's bar does not change length when you page past a bigger one.
  const maxArea = useMemo(
    () => Math.max(...filtered.map((b) => Number(b.sponsored_area_seconds ?? 0)), 1e-9),
    [filtered]
  )

  const pageItems = useMemo(
    () => sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [sorted, page]
  )

  const sortIcon = (field: SortField) => (
    <ChevronDown
      className={`h-3 w-3 text-gray-400 transition-transform ${
        sortBy === field ? (sortOrder === 'desc' ? 'rotate-180' : '') : 'opacity-0'
      }`}
    />
  )

  if (!customerId) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
          <Tv className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">Select a customer to continue</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Sponsor Value Delivered</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Measured sponsor exposure and value across full game broadcasts.
        </p>
      </div>

      {/* Only when the feature is on — a locked customer has no allowance to
          be near the end of, and the lock card already says what is wrong. */}
      {!locked && !loading && <BroadcastAllowanceBanner customerId={customerId} />}

      {!locked && (
        <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => selectTab(t.id)}
                className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                  tab === t.id
                    ? 'bg-white text-blue-600 shadow-md border border-white/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {t.label}
              </button>
            )
          })}
        </div>
      )}

      {locked ? (
        <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
          <Lock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">Broadcast analysis is not enabled</p>
          <p className="text-xs text-gray-400 mt-1">
            Contact your account manager to add broadcast analysis to this organization.
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading broadcasts…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
          <Tv className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">No broadcasts published yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Analysed broadcasts appear here once they have been reviewed.
          </p>
        </div>
      ) : tab === 'overview' ? (
        // The whole library. The search box lives on the Broadcasts tab now,
        // and a filter typed there should not quietly narrow a headline
        // shown on a tab where the box is not visible.
        <>
          <BroadcastLibraryTotals broadcasts={items} />
          <BroadcastValueDonuts broadcasts={items} all={items} />
          <BroadcastSponsorCards broadcasts={items} customerId={customerId}
            onSelect={(partnerId) => navigate(`/broadcasts/sponsors/${partnerId}`)} />
        </>
      ) : (
        <Card padding="sm" className="space-y-3">
          <BroadcastToolbar
            value={search}
            onChange={setSearch}
            placeholder="Search broadcasts, venues, leagues or feeds..."
            onRefresh={() => { void refresh() }}
            refreshing={refreshing}
            refreshLabel="Refresh broadcasts"
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {/* Identity folded into two columns. League and file count
                      are gone: a customer with one league learns nothing
                      from a column repeating its name, and the file count is
                      an artefact of how footage arrived rather than
                      anything about the broadcast. The width they held now
                      carries value. */}
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('title')}>
                    <div className="flex items-center space-x-1">
                      <span className={th}>Broadcast</span>{sortIcon('title')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('event_date')}>
                    <div className="flex items-center space-x-1">
                      <span className={th}>Aired</span>{sortIcon('event_date')}
                    </div>
                  </th>
                  <th className={`px-3 py-2 text-left ${th}`}>Feed</th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('viewers')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span className={th}>Viewers</span>{sortIcon('viewers')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('presence')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span className={th}>On screen</span>{sortIcon('presence')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('sponsors')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span className={th}>Sponsors</span>{sortIcon('sponsors')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('area_seconds')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span className={`${th} whitespace-nowrap`}>Area-seconds</span>{sortIcon('area_seconds')}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('value')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span className={`${th} whitespace-nowrap`}>Sponsor Value</span>{sortIcon('value')}
                    </div>
                  </th>
                  <th className={`px-3 py-2 text-right ${th}`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageItems.map((b) => {
                  const f = rowFigures(b)
                  return (
                  <tr
                    key={b.id}
                    // Exposure, not Brands: what the broadcast was worth is
                    // the question someone opens a broadcast with. The two
                    // explicit buttons on the right still name their own
                    // destinations for when it is not.
                    onClick={() => navigate(`/broadcasts/${b.id}/exposure`)}
                    className="hover:bg-blue-50/60 cursor-pointer transition-colors group"
                  >
                    <td className="px-3 py-2.5">
                      <p className="text-xs font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                        {b.title}
                      </p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                        {b.venue_name && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" strokeWidth={1.75} />
                            {b.venue_name}
                          </span>
                        )}
                        {b.competition_stage_name && (
                          <span className="text-gray-400">· {b.competition_stage_name}</span>
                        )}
                      </p>
                    </td>

                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <p className="text-xs text-gray-900">{formatEventDate(b.event_date) || '—'}</p>
                      <p className="text-[11px] text-gray-400 tabular-nums">
                        {formatDuration(b.duration_ms)}
                      </p>
                    </td>

                    <td className="px-3 py-2.5">
                      {b.channel_name ? (
                        <>
                          <p className="text-xs text-gray-900">{b.channel_name}</p>
                          {b.channel_type && (
                            <p className="text-[11px] text-gray-400">{formatChannelType(b.channel_type)}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-gray-400">—</p>
                      )}
                    </td>

                    {/* Never a bare number: the tooltip names where it came
                        from, and an estimate says so in the cell too. */}
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {b.viewers == null ? (
                        <p className="text-xs text-gray-400">—</p>
                      ) : (
                        <Tooltip content={describeViewers(b) ?? ''} placement="left">
                          <span className="cursor-help">
                            <span className="text-xs font-medium text-gray-900 tabular-nums">{formatViewers(b.viewers)}</span>
                            {b.viewers_is_estimate && <span className="ml-1 text-[10px] text-gray-400">est.</span>}
                          </span>
                        </Tooltip>
                      )}
                    </td>

                    <td className="px-3 py-2.5 text-right">
                      <p className="text-xs font-medium text-gray-900 tabular-nums">
                        {f.presence === null ? '—' : `${(f.presence * 100).toFixed(0)}%`}
                      </p>
                      {f.presence !== null && (
                        <span className="mt-1 block h-1 w-16 ml-auto rounded-full bg-gray-100 overflow-hidden">
                          <span className="block h-full rounded-full bg-indigo-500"
                                style={{ width: `${Math.max(f.presence * 100, 2)}%` }} />
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-2.5 text-right">
                      <p className="text-xs font-medium text-gray-900 tabular-nums">
                        {b.sponsored_brands ?? '—'}
                      </p>
                    </td>

                    {/* The bar is scaled to the largest broadcast in view, so
                        its length agrees with the number above it. It used to
                        draw share of exposure, which put a 21-area-sec game
                        level with a 337 one whenever both were mostly sold. */}
                    <td className="px-3 py-2.5 text-right">
                      <p className="text-xs font-medium text-gray-900 tabular-nums">
                        {formatAreaSeconds(Number(b.sponsored_area_seconds ?? 0))}
                      </p>
                      <span className="mt-1 block h-1 w-16 ml-auto rounded-full bg-gray-100 overflow-hidden">
                        <span className="block h-full rounded-full bg-blue-500"
                              style={{ width: `${Math.max((Number(b.sponsored_area_seconds ?? 0) / maxArea) * 100, 2)}%` }} />
                      </span>
                    </td>

                    {/* NOT PRICED IS SAID, not left blank. It is a thing
                        somebody can go and fix — an audience and a rate card
                        — and a blank cell reads as zero value. */}
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {f.value === null ? (
                        <Tooltip content="No audience recorded yet, so exposure cannot be priced." placement="left">
                          <span className="text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 cursor-help">
                            Not priced
                          </span>
                        </Tooltip>
                      ) : (
                        <p className="text-sm font-bold text-emerald-800 tabular-nums">
                          {formatMoney(f.value, currencyOf(b))}
                        </p>
                      )}
                    </td>

                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip content="View unsponsored brands" placement="left">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/broadcasts/${b.id}`) }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Tag className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip content="View broadcast details" placement="left">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/broadcasts/${b.id}/details`) }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <BarChart3 className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
            {sorted.length === 0 && (
              <p className="py-8 text-center text-xs text-gray-400">
                No broadcasts match “{search}”
              </p>
            )}
          </div>
          <BroadcastPagination
            page={page}
            perPage={PER_PAGE}
            total={sorted.length}
            unfilteredTotal={items.length}
            noun="broadcasts"
            onPage={setPage}
          />
        </Card>
      )}
    </div>
  )
}

export default BroadcastsPage
